// The full-screen canvas dim (visuals §11; backlog R3-2): black at opacity `dim`, the last draw of
// every frame, eased linearly toward a target at a rate the app picks (ui-qol §3):
//   menu on boot 0.5 (instant) · first own cell 0 at −1.5/s · Esc 0.5 at +1.25/s ·
//   own death 0.5 at +0.16/s · spectate 0 at −1.5/s.
// `setDim(target, rate ≤ 0)` jumps at once.

import type { Ctx2D } from './canvas.js';

export const DIM_RATES = Object.freeze({
  /** Menu (home) idle before the first play: instant. */
  boot: 0,
  /** Play pressed, from the first own cell drawn; also spectate start. */
  undim: 1.5,
  /** Esc while alive or spectating. */
  esc: 1.25,
  /** Own death. */
  death: 0.16,
});

/** The menu dim target (visuals §11). */
export const MENU_DIM = 0.5;

function clamp01(v: number): number {
  return v <= 0 ? 0 : v >= 1 ? 1 : v;
}

export class DimOverlay {
  private value = 0;
  private goal = 0;
  private rate = 0;

  /** Current opacity 0–1. */
  get dim(): number {
    return this.value;
  }

  /** Current target opacity. */
  get target(): number {
    return this.goal;
  }

  /** Eases linearly toward `target` at `ratePerSec` opacity per second (≤ 0 or ∞ = at once). */
  setDim(target: number, ratePerSec: number): void {
    this.goal = clamp01(Number.isFinite(target) ? target : 0);
    if (!(ratePerSec > 0) || !Number.isFinite(ratePerSec)) {
      this.value = this.goal;
      this.rate = 0;
    } else {
      this.rate = ratePerSec;
    }
  }

  /** Advances by `dtMs` (call once per frame). Returns the new opacity. */
  step(dtMs: number): number {
    const v = this.value;
    const g = this.goal;
    if (v === g) return v;
    const d = this.rate * (dtMs > 0 && Number.isFinite(dtMs) ? dtMs : 0) * 0.001;
    this.value = v < g ? Math.min(g, v + d) : Math.max(g, v - d);
    return this.value;
  }

  /** Fills the whole backing store with black at the current opacity (screen transform). */
  draw(ctx: Ctx2D, width: number, height: number): boolean {
    const a = this.value;
    if (!(a > 0.001)) return false;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = a;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    ctx.globalAlpha = 1;
    return true;
  }
}
